// @flow
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../assets/styles/colors';
import {useCommonStyles} from '../../common/components/CommonStyle';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useMemo} from 'react';
import {extractRootDomain, extractSubdomain} from '../../../../utils/string';
import {useForm, useWatch} from 'react-hook-form';
import type {SaveGeneralSettingsRequest} from '../api';

type Props = {|
  open: boolean,
  handleClose: () => void,
  domain?: string,
  saveGeneralSettings: ({dataGeneral: SaveGeneralSettingsRequest}) => void,
  validateDomain: ({subdomain: string}) => void,
  validationResult?: boolean,
  isValidating?: boolean,
  getDomainChangeToken: () => void,
  domainChangeToken?: string,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    // width: 320,
    // height: 240,
    padding: theme.spacing(4, 0),
  },
  productContainer: {
    height: '70%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  domainRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    // alignItems: 'center',
  },
  textField: {
    maxWidth: 300,
  },
  rootDomain: {
    marginTop: 16,
    marginLeft: theme.spacing(1),
  },
  helperTextValid: {
    color: colors.success,
  },
}));

export default function DomainChangeModal(props: Props) {
  const {
    open,
    handleClose,
    domain,
    saveGeneralSettings,
    validateDomain,
    validationResult,
    isValidating,
    getDomainChangeToken,
    domainChangeToken,
  } = props;

  const {register, errors, handleSubmit, control, trigger} = useForm({
    mode: 'onChange',
  });

  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const rootDomainDataSource = useMemo(
    () => (domain ? `.${extractRootDomain(domain)}` : ''),
    [domain]
  );

  const subdomain = useWatch({
    control,
    name: 'subdomain',
  });

  const subdomainErrorType = errors.subdomain?.type;

  useEffect(() => {
    if (subdomain && (!subdomainErrorType || subdomainErrorType === 'valid')) {
      validateDomain({subdomain});
    }
  }, [subdomain, subdomainErrorType, validateDomain]);

  useEffect(() => {
    trigger();
  }, [trigger, validationResult]);

  const handleSubmitSubdomain = data => {
    if (data.subdomain && rootDomainDataSource && validationResult) {
      getDomainChangeToken();
    }
  };

  useEffect(() => {
    if (domainChangeToken && subdomain && rootDomainDataSource) {
      // const url = `${subdomain}${rootDomainDataSource}`;
      saveGeneralSettings({
        dataGeneral: {subdomainName: subdomain},
        token: domainChangeToken,
      });
    }
  }, [domainChangeToken, rootDomainDataSource, saveGeneralSettings, subdomain]);

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      <div className={classes.root}>
        <Typography className={classes.title}>Edita tu dominio</Typography>
        <div className={classes.domainRow}>
          <CustomTextField
            defaultValue={domain ? extractSubdomain(domain) : ''}
            // variant="outlined"
            fullWidth
            name="subdomain"
            error={!!errors.subdomain}
            FormHelperTextProps={{
              className: validationResult ? classes.helperTextValid : '',
            }}
            helperText={
              errors.subdomain
                ? errors.subdomain.message
                : validationResult
                ? 'Perfecto! Puedes usar este dominio'
                : '(Máximo de 30 caractéres)'
              // : validationResult===false ? 'Este dominio ya se encuentra en uso. Elegir otro por favor.' : '(Máximo de 30 caractéres)'
            }
            inputRef={register({
              required: 'Campo obligatorio',
              pattern: {
                value: /^[a-z0-9][a-z0-9]*[a-z0-9]$/,
                message:
                  'El dominio no puede contener mayúsculas ni caracteres especiales como “!%$&”',
              },
              maxLength: {
                value: 30,
                message: '(Máximo de 30 caractéres)',
              },
              validate: {
                valid: v =>
                  validationResult !== false ||
                  'Este dominio ya se encuentra en uso. Elegir otro por favor.',
              },
            })}
            type="text"
            className={classes.textField}
          />
          <Typography className={classes.rootDomain}>
            {rootDomainDataSource}
          </Typography>
        </div>
        <CustomButton
          fullWidth
          type="button"
          variant="contained"
          color="primary"
          size="large"
          className={commonClasses.saveButton}
          disabled={isValidating || !validationResult || !!errors.subdomain}
          onClick={handleSubmit(handleSubmitSubdomain)}
        >
          Guardar cambios
        </CustomButton>
      </div>
    </CustomModal>
  );
}

DomainChangeModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  domain: PropTypes.string,
  saveGeneralSettings: PropTypes.func,
  validateDomain: PropTypes.func,
  validationResult: PropTypes.bool,
  isValidating: PropTypes.bool,
  getDomainChangeToken: PropTypes.func,
  domainChangeToken: PropTypes.string,
};
